import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import authReducer from './slices/authSlice';
import storesReducer from './slices/storesSlice';
import itemsReducer from './slices/itemsSlice';
import invoicesReducer from './slices/invoicesSlice';
import eventsReducer from './slices/eventsSlice';

import settingsReducer from './slices/settingsSlice';
import customersReducer from './slices/customersSlice';
import chatSessionContextReducer from './slices/chatSessionContextSlice';
import ledgerReducer from './slices/ledgerSlice';
import openItemsReducer from './slices/openItemsSlice';
import userPreferenceReducer from './slices/userPreferenceSlice';
import buyerReducer from './slices/buyerSlice';
import posterReducer from './slices/posterSlice';

// Persist config for stores slice (only persist currentStoreId)
const storesPersistConfig = {
  key: 'stores',
  storage,
  whitelist: ['currentStoreId'],
};

const persistedStoresReducer = persistReducer(storesPersistConfig, storesReducer);

// Persist config for userPreference slice (persist all fields)
const userPreferencePersistConfig = {
  key: 'userPreference',
  storage,
};

const persistedUserPreferenceReducer = persistReducer(userPreferencePersistConfig, userPreferenceReducer);

// Persist config for events slice (only persist the event queue)
const eventsPersistConfig = {
  key: 'events',
  storage,
  whitelist: ['eventQueue'], // Only persist the event queue, not loading states
};

const persistedEventsReducer = persistReducer(eventsPersistConfig, eventsReducer);

// Persist config for invoices slice (only persist invoice cache)
const invoicesPersistConfig = {
  key: 'invoices',
  storage,
  whitelist: ['invoiceCache', 'cachedInvoiceIds'], // Only persist invoice cache
};

const persistedInvoicesReducer = persistReducer(invoicesPersistConfig, invoicesReducer);

// Persist config for buyer slice (only persist ordersByStoreId)
const buyerPersistConfig = {
  key: 'buyer',
  storage,
  whitelist: ['ordersByStoreId'], // Only persist orders, not store details or explored stores
};

const persistedBuyerReducer = persistReducer(buyerPersistConfig, buyerReducer);

// Persist config for poster slice (persist storeTypes, posterDataMapByStoreId, and related fields)
const posterPersistConfig = {
  key: 'poster',
  storage,
  whitelist: ['storeTypes', 'lastFetchedAt', 'posterDataMapByStoreId'], // Persist store types, all poster data maps by identifier, and last fetched timestamp
};

const persistedPosterReducer = persistReducer(posterPersistConfig, posterReducer);

// Combine all reducers
const appReducer = combineReducers({
  auth: authReducer,
  stores: persistedStoresReducer,
  items: itemsReducer,
  invoices: persistedInvoicesReducer,
  events: persistedEventsReducer,

  settings: settingsReducer,
  customers: customersReducer,
  chatSessionContext: chatSessionContextReducer,
  ledger: ledgerReducer,
  openItems: openItemsReducer,
  userPreference: persistedUserPreferenceReducer,
  buyer: persistedBuyerReducer,
  poster: persistedPosterReducer,
});

// Root reducer that resets state on RESET_APP_STATE
const rootReducer = (state: ReturnType<typeof appReducer> | undefined, action: any) => {
  if (action.type === 'RESET_APP_STATE') {
    // Reset all state (except for persisted slices, which will be handled by redux-persist)
    state = undefined;
  }
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;